import { ArrowRight, Building2, Check, QrCode, UserRound } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { PublicUser } from "../../shared/types";
import { BrandMark } from "../components/BrandMark";
import { Button } from "../components/Buttons";
import { detectLandingLanguage, landingCopy, landingLanguages, type AuthMode, type LandingLanguage } from "../content/landing";
import { api } from "../lib/api";

const labelSizes = ["XS", "S", "M", "FitYouX", "L", "XL"] as const;
const authModes: AuthMode[] = ["demo", "login", "signup", "brand"];


export function WelcomePage({ user, onAuthenticated }: { user: PublicUser | null; onAuthenticated: (user: PublicUser) => void }) {
  const navigate = useNavigate();
  const [language, setLanguage] = useState<LandingLanguage>(detectLandingLanguage);
  const [mode, setMode] = useState<AuthMode>("demo");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const copy = landingCopy[language];
  const [demoEnabled, setDemoEnabled] = useState(false);
  useEffect(() => { void api<{demoEnabled:boolean}>("/catalog").then(result=>setDemoEnabled(result.demoEnabled)).catch(()=>setError(copy.access.error)); }, []);
  const start = async () => { if(user?.role === "consumer") {navigate("/app");return;} setBusy(true);setError("");try {const result=await api<{user:PublicUser}>("/guest",{method:"POST"});completeAuthentication(result.user);}catch(reason){setError(reason instanceof Error ? reason.message : copy.access.error);}finally{setBusy(false);} };
  const completeAuthentication = (authenticatedUser: PublicUser) => {
    onAuthenticated(authenticatedUser);
    navigate(authenticatedUser.role === "brand" ? "/studio" : "/app", { replace: true });
  };

  useEffect(() => {
    document.documentElement.lang = language;
    try { window.localStorage.setItem("fityoux-language", language); } catch { /* Optional preference. */ }
  }, [language]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      if (mode === "demo") { await start(); return; }
      const endpoint = mode === "brand" ? "/auth/signup-brand" : `/auth/${mode}`;
      const result = await api<{ user: PublicUser }>(endpoint, {
        method: "POST",
        body: JSON.stringify({
          name: data.get("name"),
          brandName: data.get("brandName"),
          email: data.get("email"),
          password: data.get("password"),
          acceptedTerms: data.get("acceptedTerms") === "on",
        }),
      });
      completeAuthentication(result.user);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : copy.access.error);
    } finally {
      setBusy(false);
    }
  };

  const openBrandDemo = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ user: PublicUser }>("/demo/brand", { method: "POST" });
      completeAuthentication(result.user);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : copy.access.error);
    } finally {
      setBusy(false);
    }
  };

  return <main className="welcome-page welcome-page--concise">
    <header className="welcome-header">
      <BrandMark />
      <div className="welcome-header-actions">
        <nav><a href="#how">{copy.nav.how}</a><a href="#brands">{copy.nav.brands}</a><a href="#access">{copy.nav.access}</a></nav>
        <div className="language-switch" aria-label="Language">
          {landingLanguages.map((item) => <button type="button" className={language === item.code ? "active" : ""} aria-pressed={language === item.code} onClick={() => setLanguage(item.code)} key={item.code}>{item.label}</button>)}
        </div>
      </div>
    </header>

    <section className="project-hero">
      <div className="project-hero-copy">
        <h1>{copy.hero.title[0]}<br />{copy.hero.title[1]}</h1>
        <p>{copy.hero.description}</p>
        <button className="button button--primary" disabled={busy} onClick={demoEnabled || user?.role === "consumer" ? start : ()=>document.getElementById("access")?.scrollIntoView({behavior:"smooth"})}>{copy.hero.action}<ArrowRight size={18}/></button><Link className="garment-demo-link" to="/demo/tshirt">{language === "it" ? "Prova il fit su una T-shirt" : language === "es" ? "Prueba el ajuste de una camiseta" : "Explore the fit on a T-shirt"}<ArrowRight size={16}/></Link>
      </div>
      <figure className="project-label-visual">
        <img src="/assets/fityoux-label-column-v3.jpg" alt={copy.hero.imageAlt} fetchPriority="high" />
        <div className="vertical-label-content" aria-label={copy.hero.labelAria}>
          <div className="vertical-size-list">{labelSizes.map((size) => <div className={size === "FitYouX" ? "selected" : ""} key={size}><span>{size}</span><span className="size-check-box" aria-hidden="true">{size === "FitYouX" ? <Check /> : null}</span></div>)}</div>
          <a className="vertical-label-footer" href="https://fityoux.com" aria-label={copy.hero.qrAria}><img src="/assets/fityoux-home-qr.svg" alt="" /><span>fityoux.com</span></a>
        </div>
      </figure>
    </section>

    <section className="project-truth">
      <h2>{copy.project.notShopTitle}</h2>
      <p>{copy.project.notShopBody}</p>
    </section>

    <section className="project-audiences">
      <article id="how">
        <header><UserRound /><div><h2>{copy.project.userTitle}</h2><p>{copy.project.userBody}</p></div></header>
        <ol>{copy.project.userSteps.map((step, index) => <li key={step}><span>{index + 1}</span><strong>{step}</strong></li>)}</ol>
      </article>
      <article id="brands">
        <header><Building2 /><div><h2>{copy.project.brandTitle}</h2><p>{copy.project.brandBody}</p></div></header>
        <ol>{copy.project.brandSteps.map((step, index) => <li key={step}><span>{index + 1}</span><strong>{step}</strong></li>)}</ol>
      </article>
    </section>

    <section className="project-bridge"><QrCode /><div><h2>{copy.project.bridgeTitle}</h2><p>{copy.project.bridgeBody}</p></div><a href="#access">{copy.hero.action}<ArrowRight /></a></section>

    <section id="access" className="access-section access-section--concise">
      <div className="access-copy"><h2>{copy.access.title}</h2><p>{copy.access.body}</p></div>
      <div className="auth-panel">
        <div className="auth-switch">{authModes.map((item) => <button type="button" className={mode === item ? "active" : ""} onClick={() => { setMode(item); setError(""); }} key={item}>{copy.access.tabs[item]}</button>)}</div>
        <form onSubmit={submit}>
          <h2>{copy.access.headings[mode]}</h2>
          <p>{copy.access.descriptions[mode]}</p>
          {mode !== "demo" && mode !== "login" ? <label>{copy.access.fields.name}<input name="name" autoComplete="name" required /></label> : null}
          {mode === "brand" ? <label>{copy.access.fields.brand}<input name="brandName" autoComplete="organization" required /></label> : null}
          {mode !== "demo" ? <><label>{copy.access.fields.email}<input name="email" type="email" autoComplete="email" required /></label><label>{copy.access.fields.password}<input name="password" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={8} required /></label></> : <p className="guest-entry-note">{language === "it" ? "Prova libera, senza email o password. I dati della prova scadono dopo 24 ore." : language === "es" ? "Prueba sin correo ni contraseña. Los datos de prueba caducan a las 24 horas." : "Try without email or password. Demo data expires after 24 hours."}</p>}
          {mode !== "demo" && mode !== "login" ? <label className="legal-consent"><input name="acceptedTerms" type="checkbox" required /><span>{copy.access.consentBefore}<Link to="/terms">{copy.access.terms}</Link>{copy.access.consentMiddle}<Link to="/privacy">{copy.access.privacy}</Link>{copy.access.consentAfter}</span></label> : null}
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          {mode === "demo" ? <div className="demo-form-actions"><Button disabled={busy || !demoEnabled} type="submit">{busy ? copy.access.buttons.busy : copy.access.buttons.demo}<ArrowRight size={17} /></Button><Button disabled={busy || !demoEnabled} variant="secondary" type="button" onClick={() => void openBrandDemo()}>{copy.access.buttons.brandDemo}<ArrowRight size={17} /></Button></div> : <Button disabled={busy} type="submit">{busy ? copy.access.buttons.busy : copy.access.buttons[mode]}<ArrowRight size={17} /></Button>}
        </form>
      </div>
    </section>

    <footer className="welcome-footer"><BrandMark /><span>{copy.footer.line}</span><nav><Link to="/privacy">{copy.footer.privacy}</Link><Link to="/terms">{copy.footer.terms}</Link></nav></footer>
  </main>;
}
